import {
  ActionError,
  Button,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  Select,
} from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import {
  useCreateTemplate,
  useTemplates,
  type Audience,
  type Category,
  type Template,
  type Version,
} from './api'
import { AUDIENCE_CATEGORIES, AUDIENCE_LABELS, CATEGORY_LABELS, CATEGORY_TYPE } from './labels'
import { copyMarkdown, starterMarkdown } from './starters'

/**
 * Novo documento (docs/areas/documentos-e-assinaturas.md, "Novo documento"; RN-DOC-42).
 * Arquétipo: cadastro, um bloco. O texto nasce da estrutura do tipo, em branco ou da cópia de outro
 * documento; daqui a pessoa vai direto para o editor.
 */

type Start = 'structure' | 'blank' | 'copy'
const AUDIENCES = Object.keys(AUDIENCE_LABELS) as Audience[]

export function NewTemplatePage() {
  const navigate = useNavigate()
  const api = useApi()
  const create = useCreateTemplate()

  const [name, setName] = useState('')
  const [audience, setAudience] = useState<Audience>('patient')
  const [category, setCategory] = useState<Category>('informed_consent')
  const [procedure, setProcedure] = useState('')
  const [start, setStart] = useState<Start>('structure')
  const [copySearch, setCopySearch] = useState('')
  const [copyFrom, setCopyFrom] = useState<Template | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [preparing, setPreparing] = useState(false)

  const debouncedCopySearch = useDebounced(copySearch.trim())
  const sources = useTemplates({
    filter: 'published',
    search: debouncedCopySearch || undefined,
  })

  const isConsent = category === 'informed_consent'
  const nameError =
    name.trim().length < 3
      ? 'Dê um nome ao documento, com pelo menos 3 caracteres.'
      : name.trim().length > 120
        ? 'O nome pode ter no máximo 120 caracteres.'
        : null
  const procedureError =
    isConsent && procedure.trim() === ''
      ? 'Informe o procedimento a que este termo se aplica.'
      : null
  const startError = start === 'copy' && !copyFrom ? 'Escolha o documento que será copiado.' : null
  const serverFields = fieldErrors(create.error)
  // A API também recusa em campos que o formulário não tem (o texto do rascunho, o público, uma
  // regra conferida no banco): sem campo na tela, a mensagem aparece inteira.
  const hasFieldOnScreen = Boolean(
    serverFields.name ?? serverFields.category ?? serverFields.procedure,
  )
  const unmappedError = hasFieldOnScreen ? undefined : Object.values(serverFields)[0]

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    setCopyError(null)
    if (nameError || procedureError || startError) return
    const cleanName = name.trim()
    const procedureLabel = isConsent ? procedure.trim() : null

    let markdown: string
    if (start === 'copy' && copyFrom) {
      setPreparing(true)
      try {
        const version = await api.get<Version>(
          `/documents/templates/${copyFrom.id}/versions/${copyFrom.currentVersion?.number ?? 1}`,
        )
        markdown = copyMarkdown(version.markdown, {
          name: cleanName,
          category,
          procedure: procedureLabel,
        })
      } catch (error) {
        setCopyError(errorMessage(error))
        setPreparing(false)
        return
      }
      setPreparing(false)
    } else {
      markdown = starterMarkdown({
        name: cleanName,
        category,
        procedure: procedureLabel,
        blank: start === 'blank',
      })
    }

    create.mutate(
      {
        name: cleanName,
        type: CATEGORY_TYPE[category],
        category,
        audience,
        ...(procedureLabel ? { procedure: procedureLabel } : {}),
        draft: { markdown },
      },
      { onSuccess: (template) => navigate(routes.admin.template(template.id, 'editor')) },
    )
  }

  const conflict =
    create.error && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.existing as { id: string } | undefined)
      : undefined

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Contratos e termos', href: routes.admin.templates }, { label: 'novo' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Novo documento"
            subtitle="Um documento nasce como rascunho. As clínicas só o enxergam depois que a primeira versão é publicada."
          />

          {(create.isError && !hasFieldOnScreen) || copyError ? (
            <ActionError
              className="mt-6"
              title="Não foi possível criar o documento"
              description={copyError ?? unmappedError ?? errorMessage(create.error)}
              actions={
                conflict ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={routes.admin.template(conflict.id)}>Abrir o existente</Link>
                  </Button>
                ) : undefined
              }
            />
          ) : null}

          <FormBlock
            className="mt-6"
            title="Identificação"
            note="O público e a categoria decidem em que fluxo o documento aparece e quais blocos ele precisa ter."
          >
            <div className="flex flex-col gap-4">
              <Field label="Nome" error={(submitted && nameError) || serverFields.name}>
                <Input
                  value={name}
                  maxLength={140}
                  placeholder="Termo de consentimento — Toxina botulínica"
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>

              <div>
                <div className="eyebrow" id="template-audience-label">
                  Público
                </div>
                <RadioGroup
                  aria-labelledby="template-audience-label"
                  className="mt-[6px] flex flex-wrap gap-x-6 gap-y-2"
                  value={audience}
                  onValueChange={(value) => {
                    const next = value as Audience
                    setAudience(next)
                    if (!AUDIENCE_CATEGORIES[next].includes(category)) {
                      setCategory(AUDIENCE_CATEGORIES[next][0]!)
                    }
                  }}
                >
                  {AUDIENCES.map((value) => (
                    <label
                      key={value}
                      className="font-body text-lead text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-3 font-light"
                    >
                      <Radio value={value} />
                      {AUDIENCE_LABELS[value]}
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <FieldGrid>
                <Field
                  label="Categoria"
                  error={serverFields.category}
                  hint={
                    audience === 'patient'
                      ? undefined
                      : 'Para Alunos, Funcionários e Terceiros: contrato, termo avulso ou outro documento.'
                  }
                >
                  <Select
                    value={category}
                    onChange={(event) => setCategory(event.target.value as Category)}
                  >
                    {AUDIENCE_CATEGORIES[audience].map((value) => (
                      <option key={value} value={value}>
                        {CATEGORY_LABELS[value]}
                      </option>
                    ))}
                  </Select>
                </Field>
                {isConsent ? (
                  <Field
                    label="Procedimento"
                    error={(submitted && procedureError) || serverFields.procedure}
                    hint="Impresso no cabeçalho do termo. O vínculo com o catálogo é feito depois, na aba Procedimentos."
                  >
                    <Input
                      value={procedure}
                      maxLength={120}
                      placeholder="Toxina botulínica"
                      onChange={(event) => setProcedure(event.target.value)}
                    />
                  </Field>
                ) : null}
              </FieldGrid>
            </div>
          </FormBlock>

          <FormBlock
            className="mt-4"
            title="Começar de"
            note="O ponto de partida do texto. Tudo pode ser mudado no editor."
          >
            <RadioGroup value={start} onValueChange={(value) => setStart(value as Start)}>
              <StartOption value="structure" title="Estrutura do tipo">
                Os blocos obrigatórios já no lugar: resumo, o que autoriza, o que pode acontecer,
                partes e assinaturas.
              </StartOption>
              <StartOption value="blank" title="Em branco">
                Só a identificação do documento. Para quem vai colar um texto pronto.
              </StartOption>
              <StartOption value="copy" title="Cópia de outro documento">
                A versão vigente de um documento publicado, com o nome novo.
              </StartOption>
            </RadioGroup>

            {start === 'copy' ? (
              <div className="mt-4">
                <Field label="Documento a copiar" error={submitted ? startError : null}>
                  <Input
                    value={copyFrom ? copyFrom.name : copySearch}
                    placeholder="Buscar pelo nome"
                    onChange={(event) => {
                      setCopyFrom(null)
                      setCopySearch(event.target.value)
                    }}
                  />
                </Field>
                {!copyFrom && debouncedCopySearch ? (
                  <ul className="border-hairline bg-surface mt-2 max-h-[240px] overflow-y-auto border">
                    {(sources.data?.data ?? []).length === 0 ? (
                      <li className="font-body text-body text-secondary px-4 py-3 font-light">
                        {sources.isPending
                          ? 'Procurando…'
                          : 'Nenhum documento publicado com esse nome.'}
                      </li>
                    ) : (
                      (sources.data?.data ?? []).map((template) => (
                        <li key={template.id} className="border-hairline border-b last:border-b-0">
                          <button
                            type="button"
                            onClick={() => setCopyFrom(template)}
                            className="hover:bg-pampas min-h-touch-desk max-desk:min-h-touch font-body text-lead text-ink block w-full cursor-pointer px-4 py-3 text-left font-light"
                          >
                            {template.name}
                            <span className="text-secondary text-[12px]">
                              {' '}
                              · {CATEGORY_LABELS[template.category]} · versão{' '}
                              {template.currentVersion?.label}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </FormBlock>
          <FormFooter
            className="mt-4"
            secondary={
              <Button variant="outline" asChild>
                <Link to={routes.admin.templates}>Cancelar</Link>
              </Button>
            }
            primary={
              <Button
                type="submit"
                loading={create.isPending || preparing}
                loadingLabel="Criando o documento…"
              >
                Criar e abrir o editor
              </Button>
            }
          />
        </ReadingColumn>
      </Page>
    </form>
  )
}

function StartOption({
  value,
  title,
  children,
}: {
  value: Start
  title: string
  children: string
}) {
  return (
    <label className="border-hairline-strong bg-surface hover:border-hairline-heavy has-[[data-state=checked]]:border-ink has-[[data-state=checked]]:bg-pampas min-h-touch flex cursor-pointer items-start gap-3 border px-4 py-[14px]">
      <Radio value={value} className="mt-[2px]" />
      <span className="flex min-w-0 flex-col">
        <span className="font-body text-lead text-ink leading-[1.3] font-light">{title}</span>
        <span className="font-body text-eyebrow text-secondary mt-1 leading-[1.5] font-light tracking-normal">
          {children}
        </span>
      </span>
    </label>
  )
}
